import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbLoader2 } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { type ConnectGooglePayload, parseConnectGooglePayload } from "./connectGoogleSchema";
import { GoogleIcon } from "./googleIcon";

export { GoogleIcon };

interface ConnectGoogleRendererProps {
  block: StreamingBlock;
}

// The backend swaps this in for a calendar result when the user lacks a Google
// connection or Calendar scope; the button runs the shared OAuth connect flow.
export const ConnectGoogleRenderer = (props: ConnectGoogleRendererProps) => {
  const rt = useChatRuntime();
  // Rich connect flow when the host provides one; plain trigger otherwise; no
  // button at all on surfaces with neither (the card still explains the state).
  const conn = rt.ui?.useProviderConnect?.("google");
  const isConnecting = () => conn?.isConnecting() ?? false;
  const connect = () => {
    rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "google", via: "button" });
    if (conn) void conn.connect();
    else rt.actions.connectProvider?.("google");
  };
  const canConnect = () => Boolean(conn || rt.actions.connectProvider);

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<ConnectGooglePayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseConnectGooglePayload(json);
  });

  return (
    <Show when={payload()}>
      {(data) => (
        <div class="@container rounded-lg border border-primary-border bg-panel p-4">
          <div class="flex items-start gap-3">
            <GoogleIcon class="size-6 shrink-0" />
            <div class="min-w-0 flex-1">
              <h3 class="text-sm font-medium text-primary-text">{rt.t("connect_google_card.title")}</h3>
              <p class="mt-1 text-xs text-muted-text">
                {data().reason === "missing_scope"
                  ? rt.t("connect_google_card.body_missing_scope")
                  : rt.t("connect_google_card.body_not_connected")}
              </p>
              <Show when={data().missingScopes?.length}>
                <div class="mt-2 flex flex-wrap items-center gap-1">
                  <span class="text-xs text-muted-text">{rt.t("connect_google_card.missing_scopes_label")}</span>
                  <For each={data().missingScopes}>
                    {(scope) => (
                      <code class="rounded bg-inset px-1 font-mono text-[0.7rem] text-primary-text">
                        {scope.replace("https://www.googleapis.com/auth/", "")}
                      </code>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </div>
          <Show when={canConnect()}>
            <button
              type="button"
              onClick={() => connect()}
              disabled={isConnecting()}
              aria-busy={isConnecting()}
              class="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 @sm:w-auto"
            >
              <Show when={!isConnecting()} fallback={<TbLoader2 class="size-4 animate-spin" />}>
                <GoogleIcon class="size-4" />
              </Show>
              <span>{isConnecting() ? rt.t("common.connecting") : rt.t("connect_google_card.button")}</span>
            </button>
          </Show>
        </div>
      )}
    </Show>
  );
};
